import React, { useState, useEffect, useCallback } from 'react'
import { colors } from './fieldTheme'
import { nowSeconds, formatRange, formatShortDate } from './fieldDates'
import { plural } from './fieldReference'
import { commentTile, photoTile, summarize } from './fieldSummary'
import { emptyNotes, newComment } from './fieldNotes'
import { buildStaffInventory } from './fieldSave'
import { interventionTile, kindTitle } from './fieldInterventions'
import { inventoryEntry, inventorySnapshot, interventionEntry } from './fieldActivity'
import { entriesAt, currentEntriesAt, newTourAction, withActions, withoutActions, withActionComment, withActionPhoto, pitchStatus, stateCounts, zoneOf, zoneLabel, whenLabel } from './fieldTours'
import { loadPitchContext, sameAsReferenceLabel } from './fieldPitchContext'
import { hasPending } from './fieldOutbox'
import { fetchStaffUsers, recordActivity, removeActivity, fetchInventoryDraft } from './fieldApi'
import { ChevronLeft, CameraIcon, CommentIcon, WrenchIcon, InventoryIcon, NoResultIcon, CheckIcon, CrossIcon } from './FieldIcons'
import { PrimaryButton, LinkButton, ErrorNote, LoadingText, Notice } from './FieldUI'
import FieldTourReport, { StatusBanner } from './FieldTourReport'
import { TourDoneScreen, TourNextScreen } from './FieldTourFinish'
import { Tile } from './FieldFollowUp'
import { UndoToast } from './FieldComments'
import { FieldPhotoSheet, FieldCommentSheet } from './FieldSheets'
import FieldInterventionSheet from './FieldInterventionSheet'

// Fiche d'un emplacement dans le tour (canvas « Plan des hébergements », M6b et M6c ; décision 50) :
// copie de Suite à donner (W16). « Photos » en premier, en pleine largeur, puis, avec le réglage
// « Tour avec inventaire » et un modèle, « Faire l'inventaire » (M6b) ; sinon inspection seule (M6c). « Rien à signaler » marque
// l'emplacement comme fait, ou enregistre un inventaire identique à la référence (décision 40).
// beforeIds : actions déjà faites à l'ouverture de la fiche (décision 15) ; les autres sont celles de ce
// passage, qui font passer les tuiles à l'état fait (W16b). Un bandeau aux couleurs du plan dit ce qui
// est enregistré ; le toucher ouvre le compte rendu en écran plein (FieldTourReport, variante A) ; les
// tuiles ne servent qu'aux actions.


export default function FieldTourPitch({ tour, pitch, pitches, user, withInventory, beforeIds, toast, onToast, onDismissToast, onChangeTour, onOpenInventory, onOpenIntervention, onBack, onNext, initialReport, onReportChange })
{
	const [now] = useState(nowSeconds())
	const [status, setStatus] = useState('loading')
	const [context, setContext] = useState(null)
	const [sheet, setSheet] = useState(null)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	// Inventaire de l'emplacement mis de côté (« Enregistrer pour plus tard ») : à reprendre depuis la tuile de l'inventaire
	const [draft, setDraft] = useState(false)
	useEffect(() => {
		let active = true
		if (!withInventory)
			return undefined
		fetchInventoryDraft(tour._id, pitch.location)
			.then(found => {
				if (active)
					setDraft(!!found)
			})
			.catch(() => null)
		return () => {
			active = false
		}
	}, [tour._id, pitch.location, withInventory])
	// initialReport : adresse /recap de l'emplacement, compte rendu ouvert s'il y a quelque chose à lire ; onReportChange
	// prévient l'app de l'ouverture et de la fermeture du compte rendu, pour que l'adresse (/recap) le suive
	const [report, setReport] = useState(() => initialReport === true && entriesAt(tour, pitch.location).length > 0)
	useEffect(() => {
		if (onReportChange)
			onReportChange(report)
	}, [report]) // eslint-disable-line react-hooks/exhaustive-deps
	// Fin de l'emplacement : null, { stage: 'done', undo } (confirmation) ou { stage: 'next' } (prochain lieu)
	const [finish, setFinish] = useState(null)
	const showNext = useCallback(() => setFinish({ stage: 'next' }), [])

	const load = useCallback(() => {
		setStatus('loading')
		loadPitchContext(pitch, now)
			.then(result => {
				setContext(result)
				setStatus('ready')
			})
			.catch(() => setStatus('error'))
	}, [pitch, now])

	useEffect(() => {
		load()
	}, [load])

	const entries = entriesAt(tour, pitch.location)
	const before = entries.filter(entry => beforeIds.includes(entry.id))
	const visit = entries.filter(entry => !beforeIds.includes(entry.id))
	const ofKind = (list, kind) => list.filter(entry => entry.kind === kind)
	const counts = stateCounts(pitches, tour)
	const hasModel = !!context && context.themes.length > 0
	const withInv = withInventory && hasModel
	const visitInventory = visit.find(entry => entry.kind === 'inventory') || null
	const beforeInventory = before.find(entry => entry.kind === 'inventory' || !entry.kind) || null
		const created = interventionTile(ofKind(visit, 'intervention'))
	const commentItems = ofKind(entries, 'comment').map(entry => ({ comment: entry.comment, themeName: null, author: entry.user_name, authorId: entry.user })).sort((a, b) => a.comment.commentTime - b.comment.commentTime)
	const photoItems = ofKind(entries, 'photo').map(entry => ({ photo: entry.photo, themeName: null }))
	const zone = zoneOf(pitch)
	const contextLine = [(pitch.location_category || '').trim(), zone ? zoneLabel(zone) : null].filter(Boolean).join(', ')
	const doneTotal = pitches.length - counts.todo
	const stay = context ? context.currentStay : null
	// Avancement : seules comptent les actions venues après un éventuel « Marquer non fait » (le contenu reste affiché)
	const current = currentEntriesAt(tour, pitch.location)
	const currentVisit = current.filter(entry => !beforeIds.includes(entry.id))
	const currentBefore = current.filter(entry => beforeIds.includes(entry.id))
	const baseState = pitchStatus(current, currentVisit, user, now)
	// Marqué non fait mais avec du contenu déjà enregistré : le bandeau reste gris et l'annonce (le compte rendu le détaille).
	// Un inventaire l'emporte sur les interventions.
	const keptInventories = ofKind(entries, 'inventory').length
	const keptInterventions = ofKind(entries, 'intervention').length
	let keptText = null
	if (keptInventories > 0)
		keptText = plural(keptInventories, 'inventaire')
	else if (keptInterventions > 0)
		keptText = plural(keptInterventions, 'intervention')
	const state = baseState.state === 'todo' && keptText ? { ...baseState, title: 'Pas encore fait avec ' + keptText + ' dans ce tour' } : baseState
	const beforeComments = ofKind(before, 'comment').length
	const beforePhotos = ofKind(before, 'photo').length
	const comments = commentTile(beforeComments, ofKind(visit, 'comment').length)
	const photos = photoTile(beforePhotos, ofKind(visit, 'photo').length)

	// Enregistre des actions et renvoie de quoi les annuler
	const commit = (actions, extraUndo) => onChangeTour(current => withActions(current, actions, nowSeconds()))
		.then(() => () => onChangeTour(current => withoutActions(current, actions.map(action => action.id))).then(extraUndo))

	const add = (actions, toastText, extraUndo) => commit(actions, extraUndo).then(undo => onToast({ text: toastText, undo }))

	const addComment = (text) => {
		const time = nowSeconds()
		const comment = newComment({ themeId: null, text, user, now: time, after: true })
		return add([newTourAction({ location: pitch.location, kind: 'comment', user, now: time, comment })], 'Commentaire ajouté')
			.then(() => setSheet(null))
	}

	const addPhotos = (list) => {
		const time = nowSeconds()
		const actions = list.map(photo => newTourAction({ location: pitch.location, kind: 'photo', user, now: time, photo: { id: photo.id, file: photo.file, url: photo.url, thumb: photo.thumb, fingerprint: photo.fingerprint, description: photo.description, user: photo.user, time: photo.time } }))
		return add(actions, plural(list.length, 'photo ajoutée', 'photos ajoutées'))
			.then(() => setSheet(null))
	}

	// Intervention liée au tour (tour_id), sans séjour : l'inventaire du tour reste hors séjour (décision 4).
	// Sans comptage, pas de réassort : le formulaire s'ouvre sur le choix Ménage ou Technique (M6b, décision 42)
	const addIntervention = ({ kind, doc, photos }) => {
		const time = nowSeconds()
		const clientRef = 'tour-' + tour._id + '-' + time + '-' + Math.random().toString(36).slice(2, 8)
		const tourDoc = { ...doc, tour_id: tour._id, client_ref: clientRef, history: doc.history.map(item => (item.type === 'creation' ? { ...item, details: 'Depuis le ' + tour.name + ', ' + pitch.location } : item)) }
		// L'intervention est créée par l'enregistrement du tour (saveTourEntries), qui la met en file d'attente sans réseau
		const action = newTourAction({ location: pitch.location, kind: 'intervention', user, now: time, intervention_id: null, type: tourDoc.type, label: tourDoc.label, description: tourDoc.description, pending_intervention: { doc: tourDoc, photos: photos || [] } })
		return onChangeTour(current => withActions(current, [action], time))
			.then(() => {
				recordActivity(interventionEntry({ doc: tourDoc, kindTitle: kindTitle(kind), pitch, user, time, inventoryEntryId: null }))
				setSheet(null)
				onToast({ text: hasPending(tour._id) ? 'Intervention enregistrée, envoi dès le retour du réseau' : 'Intervention créée' })
			})
	}

	// « Marquer non fait » : l'emplacement repasse à « Pas encore fait » sur le plan, sans rien supprimer (voir currentEntriesAt).
	// Annuler retire simplement la marque.
	const markUndone = () => {
		if (current.length === 0)
			return ;
		add([newTourAction({ location: pitch.location, kind: 'reset', user, now: nowSeconds() })], pitch.location + ' marqué non fait')
			.catch(() => setError('Rien n\'a été enregistré. Vérifiez la connexion, puis réessayez.'))
	}

	// « Rien à signaler » : inspection seule, ou inventaire identique à la référence (décision 40)
	const nothingToReport = () => {
		if (saving)
			return ;
		setSaving(true)
		setError(null)
		const time = nowSeconds()
		let done = null
		// Emplacement déjà garni (inventaire ou interventions) puis marqué non fait : « RAS » le revalide seulement, sans
		// enregistrer un nouvel inventaire identique à la référence
		if (withInv && !keptText)
		{
			const { themes, reference, accommodationId } = context
			const payload = { ...buildStaffInventory({ type: 'free', pitch, stay: null, accommodationId, themes, counts: {}, reference, notes: emptyNotes(), user, now: time }), tour_id: tour._id }
			// l'inventaire part avec l'action du tour (saveTourEntries), mis en file d'attente sans réseau
			const summary = summarize('tour', themes, {}, reference, time)
			const snapshot = inventorySnapshot({ type: 'tour', pitch, stay: null, client: null, themes, counts: {}, reference, notes: emptyNotes(), summary, interventions: [], user, time, version: 1 })
			const entry = inventoryEntry({ type: 'tour', pitch, tour, user, time, version: 1, inventoryId: null, summary, snapshot })
			const action = newTourAction({ location: pitch.location, kind: 'inventory', user, now: time, inventory_id: null, gaps: 0, same: true, pending_inventory: { payload } })
			done = commit([action], () => removeActivity(entry.id)).then(undo => {
				recordActivity(entry)
				return undo
			})
		}
		else
			done = commit([newTourAction({ location: pitch.location, kind: 'ok', user, now: time })])
		// « Rien à signaler » termine l'emplacement : confirmation, avec Annuler
		done
			.then(undo => {
				setSaving(false)
				setFinish({ stage: 'done', undo })
			})
			.catch(() => {
				setSaving(false)
				setError('Rien n\'a été enregistré. Vérifiez la connexion, puis réessayez.')
			})
	}

	const updateComment = (comment) => onChangeTour(current => withActionComment(current, comment))

	const acted = currentVisit.length > 0
	const already = currentBefore.length > 0
	let phrase = null
	let below = null
	let bottom = null
	if (acted || already)
	{
		phrase = acted ? 'Tout est enregistré. Terminez quand vous avez fini.' : 'Déjà fait dans ce tour. Complétez si besoin.'
		bottom = acted
			? <PrimaryButton icon={<CheckIcon />} onClick={() => setFinish({ stage: 'done', undo: null })}>{'Terminer ' + pitch.location}</PrimaryButton>
			: <PrimaryButton onClick={showNext}>Choisir le prochain emplacement</PrimaryButton>
	}
	else if (status === 'ready')
	{
		if (withInv && !keptText)
			below = sameAsReferenceLabel(context.reference, (time) => formatShortDate(time, now))
		else if (keptText)
			below = 'Revalide l\'emplacement, sans rien ajouter.'
		else
			phrase = withInventory && !hasModel ? 'Pas de modèle d\'inventaire : inspection seule.' : 'Rien d\'autre ? Validez pour passer au vert.'
		bottom = <PrimaryButton disabled={saving} onClick={nothingToReport}>{saving ? 'Enregistrement…' : (keptText ? 'RAS (' + keptText + ')' : 'Rien à signaler')}</PrimaryButton>
	}

	let inventoryTile = null
	if (withInv)
	{
		let subtitle = 'Inventaire du tour'
		if (visitInventory)
			subtitle = (visitInventory.same ? 'Identique au dernier, ' : 'Enregistré, ') + whenLabel(visitInventory.timestamp, now)
		else if (beforeInventory)
			subtitle = 'Inventaire du tour : ' + (beforeInventory.gaps ? plural(beforeInventory.gaps, 'écart') : 'aucun écart')
		let title = already && !visitInventory ? 'Compléter l\'inventaire' : 'Faire l\'inventaire'
		if (draft && !visitInventory)
		{
			title = 'Reprendre l\'inventaire'
			subtitle = 'Mis de côté, à terminer'
		}
		inventoryTile = <Tile icon={<InventoryIcon />} color={colors.tour} title={title} subtitle={subtitle} done={!!visitInventory} wide onClick={onOpenInventory} />
	}

	if (finish && finish.stage === 'done')
		return <TourDoneScreen pitch={pitch} text={state.text} undo={finish.undo} onContinue={showNext} onBack={() => setFinish(null)} />
	if (finish && finish.stage === 'next')
		return <TourNextScreen tour={tour} pitch={pitch} pitches={pitches} onPick={(chosen) => {
			setFinish(null)
			onNext(chosen)
		}} onBack={onBack} />

	// Compte rendu (variante A) : écran plein, ouvert en touchant le bandeau d'état
	if (report)
		return <FieldTourReport pitch={pitch} entries={entries} status={state} user={user} now={now} onOpenIntervention={onOpenIntervention} onBack={() => setReport(false)} />

	return (
		<div className="field-screen" style={{ flex: 1, display: 'flex', flexDirection: 'column', marginTop: 'calc(-1 * max(12px, env(safe-area-inset-top)))' }}>
			{/* En-tête fixe (retour d'Alexandra du 24/10/2026) : retour, emplacement en cours et avancement
			    restent visibles au défilement, comme la barre de navigation des apps natives */}
			<div style={{ position: 'sticky', top: 0, zIndex: 3, background: colors.background, paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, minHeight: 56, padding: '0 20px 0 8px' }}>
					<button
						type="button"
						aria-label="Retour au plan du tour"
						onClick={onBack}
						style={{ backgroundImage: 'none', backgroundColor: 'transparent', justifySelf: 'start', minHeight: 48, padding: '0 12px 0 4px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, fontFamily: 'inherit', cursor: 'pointer' }}
					>
						<ChevronLeft />
						Plan
					</button>
					<h1 style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, color: colors.text, textAlign: 'center', overflowWrap: 'anywhere' }}>{pitch.location}</h1>
					<span aria-label={doneTotal + ' emplacements faits sur ' + pitches.length} style={{ justifySelf: 'end', fontSize: 17, fontWeight: 700, color: colors.primary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{doneTotal + ' / ' + pitches.length}</span>
				</div>
				<div role="progressbar" aria-valuemin={0} aria-valuemax={pitches.length} aria-valuenow={doneTotal} aria-label="Avancement du tour" style={{ height: 4, background: '#E8EBF5' }}>
					<div style={{ height: '100%', background: colors.primary, width: (pitches.length ? Math.round(doneTotal * 1000 / pitches.length) / 10 : 0) + '%' }} />
				</div>
				<UndoToast toast={toast} onUndo={() => {
					const current = toast
					onDismissToast()
					Promise.resolve(current.undo()).catch(() => setError('L\'annulation n\'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.'))
				}} onDismiss={onDismissToast} />
			</div>

			{contextLine && <p style={{ padding: '20px 20px 0', fontSize: 17, fontWeight: 600, color: colors.text }}>{contextLine}</p>}
			{stay && <p style={{ padding: '0 20px', fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{'Séjour n° ' + stay.stay_number + ', du ' + formatRange(stay.arrival_time, stay.departure_time, now)}</p>}

			<StatusBanner status={state} onOpen={entries.length > 0 ? () => setReport(true) : null} />

			{status === 'loading' && <LoadingText>Chargement de l'emplacement…</LoadingText>}
			{status === 'error' && <Notice icon={<NoResultIcon />} title="Impossible de charger l'emplacement" hint="Vérifiez la connexion, puis réessayez." action={<div style={{ marginLeft: -12 }}><LinkButton onClick={load}>Réessayer</LinkButton></div>} />}

			{status === 'ready' && (
				<React.Fragment>
					<p style={{ padding: '20px 20px 12px', fontSize: 17, fontWeight: 600, color: colors.text }}>Tout est facultatif.</p>
					<div role="group" aria-label="Suite à donner" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '0 20px' }}>
						<Tile icon={<CameraIcon size={46} color="#FFFFFF" />} color="#0F766E" title="Photos" subtitle={photos.subtitle} done={photos.done} wide onClick={() => setSheet('photo')} />
						{inventoryTile}
						<Tile icon={<WrenchIcon />} color="#8124FF" title="Créer une intervention" subtitle={created.done ? created.subtitle : 'Ménage ou technique'} done={created.done} wide onClick={() => setSheet('intervention')} />
						<Tile icon={<CommentIcon />} color="#B45309" title="Commentaire" subtitle={comments.subtitle} done={comments.done} wide onClick={() => setSheet('comment')} />
						{current.length > 0 && <Tile icon={<CrossIcon />} color="#6B7280" title="Marquer non fait" subtitle="Pas encore fait" done={false} wide onClick={markUndone} />}
					</div>
					<div aria-hidden="true" style={{ height: 24, flex: 'none' }} />
				</React.Fragment>
			)}

			{/* Action principale fixée en bas, sous le pouce (charte apps terrain) */}
			<div style={{ position: 'sticky', bottom: 0, zIndex: 2, marginTop: 'auto', display: error || phrase || bottom || below ? 'flex' : 'none', flexDirection: 'column', gap: 8, padding: '12px 20px', paddingBottom: 'max(20px, env(safe-area-inset-bottom))', background: colors.background, borderTop: '1px solid ' + colors.separator }}>
				{error && <ErrorNote>{error}</ErrorNote>}
				{phrase && <p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>{phrase}</p>}
				{bottom}
				{below && <p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>{below}</p>}
				{(acted || already) && <div style={{ display: 'flex', justifyContent: 'center' }}><LinkButton onClick={onBack}>Retour au plan</LinkButton></div>}
			</div>

			{sheet === 'comment' && (
				<FieldCommentSheet
					title="Commentaire"
					listTitle="Déjà saisis"
					items={commentItems}
					user={user}
					now={now}
					placeholder={'Ce que vous avez constaté sur ' + pitch.location}
					saveLabel="Enregistrer le commentaire"
					singleScroll
					onSave={addComment}
					onUpdate={updateComment}
					onClose={() => setSheet(null)}
				/>
			)}
			{sheet === 'photo' && (
				<FieldPhotoSheet
					title="Photos"
					subtitle={photoItems.length > 0 ? plural(photoItems.length, 'photo déjà déposée', 'photos déjà déposées') : null}
					saveLabel="Enregistrer les photos"
					singleScroll
					user={user}
					existing={photoItems}
					onChangeExisting={(photo) => onChangeTour(current => withActionPhoto(current, photo))}
					onSave={addPhotos}
					onClose={() => setSheet(null)}
				/>
			)}
			{sheet === 'intervention' && (
				<FieldInterventionSheet
					start="problem"
					defaultUrgency="IMPORTANT"
					singleScroll
					missing={[]}
					restockOffered={false}
					restockDone={false}
					inventoryPhotos={photoItems}
					inventoryComments={commentItems.map(item => ({ id: item.comment.id, text: item.comment.comment.trim(), user: item.author, userId: item.authorId, time: item.comment.commentTime }))}
					pitch={pitch}
					stay={null}
					user={user}
					loadUsers={fetchStaffUsers}
					onCreate={addIntervention}
					onClose={() => setSheet(null)}
				/>
			)}
		</div>
	)
}
